import { useMemo, useState } from "react";
import AddCustomerModal from "../../../components/Forms/AddCustomerModal";
import ViewCustomerModal from "../../../components/Forms/ViewCustomerModal";
import EditCustomerModal from "../../../components/Forms/EditCustomerModal";
import CashierLayout from "../Layout";
import type { Customer } from "../../../types/store";
import { useStores } from "../../../hooks/useStores";
import { useCustomers } from "../../../hooks/useCustomers";
import { useAppDispatch } from "../../../hooks";
import { fetchCustomers, softDeleteCustomers } from "../../../slices/customerSlice";
import { Edit, Eye, Plus, RefreshCcwIcon, Trash, Users } from "lucide-react";
import { useSubscriptionGuard } from "../../../hooks/useSubscriptionGuard";
import EntityLimitCard from "../../../components/EntityLimitCard";
import SubscriptionGuardModal from "../../../components/SubscriptionGuardModal";


export default function CustomersPage() {

  const { stores } = useStores();
  const { permissions } = useSubscriptionGuard();
  const { customers, loading, error } = useCustomers();
  const dispatch = useAppDispatch();

//   MODAL VISIBILITY STATES
  const [openAddCustomerModal, setOpenAddCustomerModal] = useState(false);
  const [openViewCustomerModal, setOpenViewCustomerModal] = useState(false);
  const [openEditCustomerModal, setOpenEditCustomerModal] = useState(false);
  const [showSubscriptionGuardModal, setShowSubscriptionGuardModal] = useState(false);
 
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedStore, setSelectedStore] = useState("all");
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const toggleSelectAll = (checked: boolean) => {
    if (checked) setSelectedIds(customers.map((customer) => customer.id));
    else setSelectedIds([]);
  };

   const deleteSelected = async () => {
        if (!selectedIds.length) return;
        if (!confirm(`Delete ${selectedIds.length} customer(s)?`)) return;

        await dispatch(softDeleteCustomers(selectedIds));
        setSelectedIds([]);
    };


    const filteredCustomers = useMemo(() => {
    return customers.filter((c) => {
        let matchesName = true;
        let matchesStore = true;

        if (searchTerm.trim() !== "") {
        matchesName = `${c.firstname} ${c.lastname} ${c.othername}`
            .toLowerCase()
            .includes(searchTerm.toLowerCase());
        }

        if (selectedStore !== "all") {
        matchesStore = c.store_id === selectedStore;
        }

        return matchesName && matchesStore;
    });
    }, [customers, searchTerm, selectedStore]);



//   const handleAdd = (newCustomer: Customer) => {
//     setCustomers((prev) => [...prev, newCustomer]);
//     setModalType(null);
//   };

//   const handleEdit = (updated: Customer) => {
//     setCustomers((prev) => prev.map((c) => (c.id === updated.id ? updated : c)));
//     setModalType(null);
//   };

  return (
    <CashierLayout>
        <div className="p-6 space-y-6">
        {/* Header */}
        <header className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            {/* Top section: Search & Filter */}
            <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
                {/* Search Bar */}
                <input
                    type="text"
                    placeholder="Search customers..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full sm:w-64 border border-blue-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-100"
                />

                {/* Store Filter */}
                <select
                    className="border border-blue-200 rounded-md px-3 py-2 text-sm focus:ring focus:ring-blue-100"
                    value={selectedStore}
                    onChange={(e) => setSelectedStore(e.target.value)}
                >
                    <option value="all">All Stores</option>
                    {stores.map((store) => (
                        <option key={store.id} value={store.id}>
                            {store.name}
                        </option>
                    ))}
                </select>

                <EntityLimitCard
                    limit={permissions?.customerLimit as number}
                    limitReached={customers.length >= (permissions?.customerLimit ?? Infinity)}
                    value={customers.length}
                    entity="customers"
                />
            </div>

            {/* Bottom / Right section: Refresh + Add Buttons */}
            <div className="flex items-center gap-3">
                <button
                    onClick={() => {
                        dispatch(fetchCustomers());
                    }}
                    className="px-2 py-2 rounded-md bg-transparent text-gray-800 border border-gray-200 hover:bg-gray-50"
                >
                    <RefreshCcwIcon size={18} />
                </button>

                <div className="flex items-center gap-2">
                    <input
                        type="checkbox"
                        onChange={(e) => toggleSelectAll(e.target.checked)}
                        checked={selectedIds.length === filteredCustomers.length && filteredCustomers.length > 0}
                        aria-label="Select all employees"
                    />
                    <button
                        disabled={!selectedIds.length}
                        onClick={deleteSelected}
                        className={`px-3 flex items-center py-2 rounded-md sm:text-xs md:text-md ${selectedIds.length ? "bg-red-600 text-white" : "bg-gray-200 text-gray-500"}`}
                    >
                        <Trash className="inline mr-2" /> 
                        <span className="sm:hidden md:flex">Delete {selectedIds.length > 0 && `(${selectedIds.length})`}</span>
                    </button>
                </div>

                <button
                    onClick={() => setOpenAddCustomerModal(true)}
                    className="flex gap-2 items-center bg-primary text-white px-4 py-2 rounded-md text-sm font-medium hover:opacity-90"
                >
                    <Plus size={16} />
                    <span className="sm:hidden md:flex">Add</span>
                </button>
            </div>
        </header>


        {/* Table */}
        <div className="overflow-x-auto border border-gray-200 rounded-lg">
        <table className="min-w-full text-sm text-left">
            <thead className="bg-blue-50">
            <tr>
                <th className="table-head">Name</th>
                <th className="table-head">Email</th>
                <th className="table-head">Phone</th>
                <th className="table-head">Purchases</th>
                <th className="table-head">Actions</th>
            </tr>
            </thead>

            <tbody>
            {loading ? (
                // Skeleton Loading Rows
                Array.from({ length: 5 }).map((_, i) => (
                <tr key={i} className="border-t animate-pulse">
                    <td className="px-4 py-3">
                        <div className="h-4 w-32 bg-gray-200 rounded"></div>
                    </td>
                    <td className="px-4 py-3">
                        <div className="h-4 w-40 bg-gray-200 rounded"></div>
                    </td>
                    <td className="px-4 py-3">
                        <div className="h-4 w-24 bg-gray-200 rounded"></div>
                    </td>
                    <td className="px-4 py-3">
                        <div className="h-4 w-12 bg-gray-200 rounded"></div>
                    </td>
                    <td className="px-4 py-3 flex gap-2 justify-end">
                        <div className="h-4 w-8 bg-gray-200 rounded"></div>
                        <div className="h-4 w-8 bg-gray-200 rounded"></div>
                    </td>
                </tr>
                ))
            ) : filteredCustomers.length === 0 ? (
                <tr>
                  <td colSpan={5} className="py-16 text-center">
                    <div className="flex flex-col items-center gap-3 text-gray-500">
                      <Users size={40} className="text-gray-300" />
                      <p className="font-medium text-gray-600">
                        {customers.length === 0 ? "No customers yet" : "No customers match your search"}
                      </p>
                      <p className="text-sm">
                        {customers.length === 0
                          ? "Add your first customer to start tracking purchases."
                          : "Try a different name, email, or store filter."}
                      </p>
                      {customers.length === 0 && (
                        <button
                          onClick={() => setOpenAddCustomerModal(true)}
                          className="mt-2 flex items-center gap-2 px-4 py-2 rounded-md bg-primary text-white text-sm hover:opacity-90"
                        >
                          <Plus size={16} /> Add Customer
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
            ) : (
                // Actual Customer Rows
                filteredCustomers.map((customer) => {
                const fullName = [customer.firstname, customer.othername, customer.lastname]
                    .filter(Boolean)
                    .join(" ");
                const purchases = customer.Sale?.length ?? 0;
                const selected = selectedIds?.includes(customer.id)

                return (
                    <tr key={customer.id} className="border-t hover:bg-blue-50/40">
                    <td className="px-4 py-3 flex items-center gap-2">
                        <input
                            type="checkbox"
                            checked={selected}
                            onChange={(e) => {
                                if (e.target.checked) {
                                    // Add ID to selectedIds
                                    setSelectedIds((prev) => [...prev, customer.id]);
                                } else {
                                    // Remove ID from selectedIds
                                    setSelectedIds((prev) => prev.filter((id) => id !== customer.id));
                                }
                            } }
                            aria-label={`Select ${fullName}`}
                        />
                        {fullName}
                    </td>
                    <td className="px-4 py-3">{customer.email || "-"}</td>
                    <td className="px-4 py-3">{customer.phone_number || "-"}</td>
                    <td className="px-4 py-3 font-semibold text-blue-700">{purchases}</td>
                    <td className="px-4 py-3 flex gap-2">
                        <button
                            onClick={() => {
                                setSelectedCustomer(customer);
                                setOpenViewCustomerModal(true);
                            }}
                            className="flex gap-1 items-center bg-primary text-white p-2 hover:bg-blue-900 rounded-lg"
                        >
                            <Eye size={16} /> 
                        View
                        </button>

                        <button
                            onClick={() => {
                                setSelectedCustomer(customer);
                                setOpenEditCustomerModal(true);
                            }}
                            className="flex gap-1 items-center bg-white text-primary shadow-sm p-2 hover:shadow-lg rounded-lg"
                        >
                            <Edit size={16} />
                            Edit
                        </button>
                    </td>
                    </tr>
                );
                })
            )}
            </tbody>
        </table>
        </div>

        {error && (
            <div className="flex items-center justify-between bg-red-50 text-red-600 border border-red-200 p-3 rounded-md mb-4">
                <span>{error}</span>
                <button
                onClick={() => dispatch(fetchCustomers())}
                className="px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700"
                >
                Retry
                </button>
            </div>
        )}

        {/* Modals */}
        {
            <AddCustomerModal 
                onClose={() => setOpenAddCustomerModal(false)} 
                isOpen={openAddCustomerModal}
                stores={stores}    
            />
        }
            <ViewCustomerModal 
                customer={selectedCustomer} 
                onClose={() => setOpenViewCustomerModal(false)} 
                isOpen={openViewCustomerModal}    
            />
            <EditCustomerModal 
                customer={selectedCustomer} 
                onClose={() => {
                    setOpenEditCustomerModal(false);
                    setSelectedCustomer(null);
                }} 
                isOpen={openEditCustomerModal}
                stores={stores}    
            />
        </div>

        {/* SUBSCRIPTION GUARD */}
        <SubscriptionGuardModal
            isOpen={showSubscriptionGuardModal}
            onClose={() => setShowSubscriptionGuardModal(false)}
            type="customers"
        />


    </CashierLayout>
  );
}
